import { NetworkStatus } from "@apollo/client";
import { useTimeTrackingPopoverItemsQuery } from "@src/__generated__/graphql";
import { appStore } from "@src/stores/AppStore";
import type { TimeTrackingPopoverStore } from "@src/stores/TimeTrackingPopoverStore";
import { onError } from "@src/utils/apollo";
import { toApiDate } from "@src/utils/dates";
import { useStore } from "@src/utils/hooks";
import { endOfWeek, startOfWeek } from "date-fns";
import { useEffect, useState } from "react";

const pollInterval = 60 * 1000; // 1 minute

export const useFetchTimeTrackingItems = (store: TimeTrackingPopoverStore) => {
  const { authStore } = useStore();
  const [dates, setDates] = useState(() => getStartEndOfWeek(new Date()));

  useEffect(() => {
    // Update dates due to transition between days
    const timer = setInterval(() => {
      setDates(getStartEndOfWeek(new Date()));
    }, pollInterval);

    return () => clearInterval(timer);
  }, []);

  const { loading, refetch, networkStatus } = useTimeTrackingPopoverItemsQuery({
    notifyOnNetworkStatusChange: true,
    pollInterval,
    skip: !store.popoverActive,
    variables: {
      user_id: authStore.user!.id,
      from: toApiDate(dates.start),
      to: toApiDate(dates.end),
    },
    onCompleted(data) {
      store.setEntries(data);
    },
    ...onError(),
  });

  store.refetchEntries = refetch;

  // We just want to respond to loading and refetch state
  if (
    [
      NetworkStatus.loading,
      NetworkStatus.setVariables,
      NetworkStatus.ready,
      NetworkStatus.refetch,
    ].includes(networkStatus)
  ) {
    return loading;
  }

  return false;
};

const getStartEndOfWeek = (date: Date) => ({
  start: startOfWeek(date, {
    weekStartsOn: appStore.workspaceStore.settings?.startOfWeekNumber,
  }),
  end: endOfWeek(date, {
    weekStartsOn: appStore.workspaceStore.settings?.startOfWeekNumber,
  }),
});
